import { PalmettoIcon, PalmettoTextInput } from '@palmetto/dsm-components';
import { useCallback, useMemo } from 'react';
import { Control, Controller } from 'react-hook-form';

import {
  SystemLossThresholdsFormValues,
  validateCountyName,
  validateMinPercent,
  validateSnowPercent,
} from './formModel';

interface CountyRowProps {
  stateIndex: number;
  countyIndex: number;
  control: Control<SystemLossThresholdsFormValues>;
  onRemove: (index: number) => void;
  disabled?: boolean;
}

const PERCENT_SUFFIX = '%';

export const CountyRow = ({ stateIndex, countyIndex, control, onRemove, disabled }: CountyRowProps) => {
  const prefix = `entries.${stateIndex}.counties.${countyIndex}` as const;

  const nameRules = useMemo(
    () => ({
      validate: (value: string, formValues: SystemLossThresholdsFormValues) =>
        validateCountyName(value, formValues.entries[stateIndex]?.counties ?? [], countyIndex),
    }),
    [stateIndex, countyIndex],
  );

  const handleRemove = useCallback(() => onRemove(countyIndex), [onRemove, countyIndex]);

  return (
    <div data-testid={`county-row-${stateIndex}-${countyIndex}`} className="flex flex-wrap items-start gap-8">
      <div className="w-[200px]">
        <Controller
          name={`${prefix}.name`}
          control={control}
          rules={nameRules}
          render={({ field, fieldState }) => (
            <PalmettoTextInput
              id={`county-name-${stateIndex}-${countyIndex}`}
              label="County"
              helperText="Without the word County"
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              disabled={disabled}
              validationMessage={
                fieldState.error ? { type: 'error', text: fieldState.error.message ?? '' } : undefined
              }
            />
          )}
        />
      </div>
      <div className="w-[160px]">
        <Controller
          name={`${prefix}.snowLossPercent`}
          control={control}
          rules={{ validate: validateSnowPercent }}
          render={({ field, fieldState }) => (
            <PalmettoTextInput
              id={`county-snow-${stateIndex}-${countyIndex}`}
              label="Snow loss"
              suffix={PERCENT_SUFFIX}
              inputMode="decimal"
              helperText="Blank if none"
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              disabled={disabled}
              validationMessage={
                fieldState.error ? { type: 'error', text: fieldState.error.message ?? '' } : undefined
              }
            />
          )}
        />
      </div>
      <div className="w-[180px]">
        <Controller
          name={`${prefix}.minSystemLossPercent`}
          control={control}
          rules={{ validate: validateMinPercent }}
          render={({ field, fieldState }) => (
            <PalmettoTextInput
              id={`county-min-${stateIndex}-${countyIndex}`}
              label="Min system loss"
              suffix={PERCENT_SUFFIX}
              inputMode="decimal"
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              disabled={disabled}
              validationMessage={
                fieldState.error ? { type: 'error', text: fieldState.error.message ?? '' } : undefined
              }
            />
          )}
        />
      </div>
      <div className="pt-8">
        <button
          type="button"
          onClick={handleRemove}
          disabled={disabled}
          className="cursor-pointer border-none bg-transparent p-8 leading-none text-text-tertiary hover:text-text-primary disabled:cursor-not-allowed disabled:opacity-50"
          aria-label="Remove county override"
        >
          <PalmettoIcon name="trash" size="xs" />
        </button>
      </div>
    </div>
  );
};
